import { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Stepper } from "@/components/ui/stepper";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ErrorText } from "@/components/ui/error-text";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { store } from "../../store";
import { defaultPageSize } from "../../utils/constants";
import type { IntegrationType, LifecycleState, Robot, RobotClass, RobotType } from "../types";

const robotTypeOptions: { value: RobotType; label: string }[] = [
    { value: "unit_load", label: "page.robots.type.unitLoad" },
    { value: "forklift", label: "page.robots.type.forklift" },
    { value: "tugger", label: "page.robots.type.tugger" },
    { value: "pallet_mover", label: "page.robots.type.palletMover" },
    { value: "cart", label: "page.robots.type.cart" },
    { value: "cleaner", label: "page.robots.type.cleaner" },
];

const lifecycleOptions: { value: LifecycleState; label: string }[] = [
    { value: "planned", label: "page.robots.lifecycle.planned" },
    { value: "commissioning", label: "page.robots.lifecycle.commissioning" },
    { value: "active", label: "page.robots.lifecycle.active" },
    { value: "suspended", label: "page.robots.lifecycle.suspended" },
    { value: "maintenance", label: "page.robots.lifecycle.maintenance" },
    { value: "decommissioned", label: "page.robots.lifecycle.decommissioned" },
];

const integrationOptions: { value: IntegrationType; label: string }[] = [
    { value: "vendor_api", label: "page.robots.integration.vendorApi" },
    { value: "simulation", label: "page.robots.integration.simulation" },
    { value: "none", label: "page.robots.integration.none" },
];

type IdentityForm = {
    name: string;
    manufacturer: string;
    model: string;
    kind: RobotClass;
    type: RobotType;
};

type PlacementForm = {
    departmentId?: string;
    lifecycle: LifecycleState;
};

type IntegrationForm = {
    integration: IntegrationType;
    endpoint: string;
};

export type RobotForm = IdentityForm & PlacementForm & IntegrationForm;

const STEPS = [
    "page.robots.formDialog.steps.identity",
    "page.robots.formDialog.steps.placement",
    "page.robots.formDialog.steps.integration",
] as const;

const Field = ({
    label,
    required,
    error,
    children,
}: React.PropsWithChildren<{
    label: string;
    required?: boolean;
    error?: string;
}>): JSX.Element => (
    <div className="grid content-start gap-2">
        <Label>
            {label}
            {required && " *"}
        </Label>
        {children}
        {error && <ErrorText>{error}</ErrorText>}
    </div>
);

type StepNavigation = {
    steps: readonly string[];
    step: number;
    isStepEnabled: (index: number) => boolean;
    onNavigate: (target: number) => void;
};

type StepProps<T> = {
    nav: StepNavigation;
    defaultValues?: Partial<T>;
    onSubmit: (values: T) => void;
    onCancel: () => void;
};

const StepForm = ({
    stepper,
    onSubmit,
    onBack,
    onCancel,
    submitLabel,
    children,
}: React.PropsWithChildren<{
    stepper: StepNavigation & { onStepClick: (target: number) => void };
    onSubmit: React.FormEventHandler<HTMLFormElement>;
    onBack?: () => void;
    onCancel: () => void;
    submitLabel: string;
}>): JSX.Element => {
    const { t } = useTranslation();

    return (
        <>
            <Stepper
                steps={stepper.steps}
                step={stepper.step}
                isStepEnabled={stepper.isStepEnabled}
                onStepClick={stepper.onStepClick}
            />
            <form
                onSubmit={onSubmit}
                className="border-border bg-card flex min-h-[420px] flex-col rounded-lg border p-6"
            >
                <div className="grid grid-cols-2 gap-x-8 gap-y-6">{children}</div>
                <div className="mt-auto flex items-center justify-between pt-6">
                    <Button type="button" variant="ghost" onClick={onCancel}>
                        {t("generic.cancel")}
                    </Button>
                    <div className="flex gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onBack}
                            disabled={onBack == null}
                        >
                            {t("page.robots.formDialog.back")}
                        </Button>
                        <Button type="submit">{submitLabel}</Button>
                    </div>
                </div>
            </form>
        </>
    );
};

const IdentityStep = ({
    nav,
    defaultValues,
    onSubmit,
    onCancel,
}: StepProps<IdentityForm>): JSX.Element => {
    const { t } = useTranslation();
    const {
        register,
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<IdentityForm>({ defaultValues });

    // Validate before jumping to any of the next steps.
    const goToStep = (target: number): void => {
        if (target > nav.step) {
            handleSubmit((values) => {
                onSubmit(values);
                nav.onNavigate(target);
            })();
        } else {
            nav.onNavigate(target);
        }
    };

    return (
        <StepForm
            stepper={{ ...nav, onStepClick: goToStep }}
            onSubmit={handleSubmit((values) => {
                onSubmit(values);
                nav.onNavigate(1);
            })}
            onCancel={onCancel}
            submitLabel={t("page.robots.formDialog.continue")}
        >
            <div className="col-span-2">
                <Field
                    label={t("page.robots.formDialog.name.label")}
                    required
                    error={errors.name && t("validation.required")}
                >
                    <Input
                        {...register("name", { required: true })}
                        errored={errors.name != null}
                    />
                </Field>
            </div>
            <Field
                label={t("page.robots.formDialog.manufacturer.label")}
                required
                error={errors.manufacturer && t("validation.required")}
            >
                <Input
                    {...register("manufacturer", { required: true })}
                    errored={errors.manufacturer != null}
                />
            </Field>
            <Field label={t("page.robots.formDialog.model.label")}>
                <Input {...register("model")} />
            </Field>
            <div className="col-span-2">
                <Field label={t("page.robots.formDialog.robotClass.label")} required>
                    <Controller
                        control={control}
                        name="kind"
                        defaultValue="agv"
                        render={({ field }) => (
                            <div className="border-border inline-flex w-fit overflow-hidden rounded-md border">
                                {(["agv", "amr"] as const).map((kind) => (
                                    <button
                                        type="button"
                                        key={kind}
                                        onClick={() => field.onChange(kind)}
                                        className={cn(
                                            "cursor-pointer px-6 py-2 text-sm",
                                            field.value === kind
                                                ? "bg-primary text-primary-foreground font-semibold"
                                                : "hover:bg-accent",
                                        )}
                                    >
                                        {kind.toUpperCase()}
                                    </button>
                                ))}
                            </div>
                        )}
                    />
                </Field>
            </div>
            <Field
                label={t("page.robots.formDialog.robotType.label")}
                required
                error={errors.type && t("validation.required")}
            >
                <Controller
                    control={control}
                    name="type"
                    rules={{ required: true }}
                    render={({ field }) => (
                        <Select value={field.value} onValueChange={field.onChange}>
                            <SelectTrigger className={cn(errors.type && "border-red-500")}>
                                <SelectValue
                                    placeholder={t("page.robots.formDialog.robotType.placeholder")}
                                />
                            </SelectTrigger>
                            <SelectContent>
                                {robotTypeOptions.map((option) => (
                                    <SelectItem value={option.value} key={option.value}>
                                        {t(option.label)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}
                />
            </Field>
        </StepForm>
    );
};

const PlacementStep = observer(
    ({ nav, defaultValues, onSubmit, onCancel }: StepProps<PlacementForm>): JSX.Element => {
        const { t } = useTranslation();
        const {
            control,
            handleSubmit,
            formState: { errors },
        } = useForm<PlacementForm>({ defaultValues });

        const departments = store.repositoryDepartments
            .byQuery({ pageSize: defaultPageSize })
            .filter((department) => department !== undefined);

        // Validate before jumping to any of the next steps.
        const goToStep = (target: number): void => {
            if (target > nav.step) {
                handleSubmit((values) => {
                    onSubmit(values);
                    nav.onNavigate(target);
                })();
            } else {
                nav.onNavigate(target);
            }
        };

        return (
            <StepForm
                stepper={{ ...nav, onStepClick: goToStep }}
                onSubmit={handleSubmit((values) => {
                    onSubmit(values);
                    nav.onNavigate(2);
                })}
                onBack={() => nav.onNavigate(0)}
                onCancel={onCancel}
                submitLabel={t("page.robots.formDialog.continue")}
            >
                <Field label={t("page.robots.formDialog.department.label")}>
                    <Controller
                        control={control}
                        name="departmentId"
                        render={({ field }) => (
                            <Select value={field.value} onValueChange={field.onChange}>
                                <SelectTrigger
                                    className={cn(errors.departmentId && "border-red-500")}
                                >
                                    <SelectValue
                                        placeholder={t(
                                            "page.robots.formDialog.department.placeholder",
                                        )}
                                    />
                                </SelectTrigger>
                                <SelectContent>
                                    {departments.map((department) => (
                                        <SelectItem value={department.id} key={department.id}>
                                            {store.i18n.formatDepartment(department)}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                    />
                </Field>
                <Field
                    label={t("page.robots.formDialog.lifecycle.label")}
                    required
                    error={errors.lifecycle && t("validation.required")}
                >
                    <Controller
                        control={control}
                        name="lifecycle"
                        rules={{ required: true }}
                        render={({ field }) => (
                            <Select value={field.value} onValueChange={field.onChange}>
                                <SelectTrigger className={cn(errors.lifecycle && "border-red-500")}>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {lifecycleOptions.map((option) => (
                                        <SelectItem value={option.value} key={option.value}>
                                            {t(option.label)}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                    />
                </Field>
            </StepForm>
        );
    },
);

const IntegrationStep = ({
    nav,
    defaultValues,
    onSubmit,
    onCancel,
    submitLabel,
}: StepProps<IntegrationForm> & { submitLabel: string }): JSX.Element => {
    const { t } = useTranslation();
    const {
        register,
        control,
        handleSubmit,
        watch,
        formState: { errors },
    } = useForm<IntegrationForm>({
        defaultValues,
    });

    const showEndpoint = watch("integration") === "vendor_api";

    return (
        <StepForm
            stepper={{ ...nav, onStepClick: nav.onNavigate }}
            onSubmit={handleSubmit(onSubmit)}
            onBack={() => nav.onNavigate(1)}
            onCancel={onCancel}
            submitLabel={submitLabel}
        >
            <Field
                label={t("page.robots.formDialog.integration.label")}
                required
                error={errors.integration && t("validation.required")}
            >
                <Controller
                    control={control}
                    name="integration"
                    defaultValue="none"
                    rules={{ required: true }}
                    render={({ field }) => (
                        <Select value={field.value} onValueChange={field.onChange}>
                            <SelectTrigger className={cn(errors.integration && "border-red-500")}>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {integrationOptions.map((option) => (
                                    <SelectItem value={option.value} key={option.value}>
                                        {t(option.label)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}
                />
            </Field>
            {showEndpoint && (
                <Field label={t("page.robots.formDialog.endpoint.label")}>
                    <Input
                        {...register("endpoint")}
                        placeholder="https://fleet-manager.example.com"
                    />
                </Field>
            )}
        </StepForm>
    );
};

export type RobotFormDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    robot?: Robot;
    onSave: (form: RobotForm) => void;
};

export const RobotFormDialog = observer(
    ({ open, onOpenChange, robot, onSave }: RobotFormDialogProps) => {
        const { t } = useTranslation();
        const [step, setStep] = useState(0);

        // Sub-forms, will be combined into the robot entity at the end.
        const [identity, setIdentity] = useState<IdentityForm>();
        const [placement, setPlacement] = useState<PlacementForm>();
        const [integration, setIntegration] = useState<IntegrationForm>();

        useEffect(() => {
            if (open) {
                setStep(0);
                setIdentity(
                    robot
                        ? {
                              name: robot.name,
                              manufacturer: robot.manufacturer,
                              model: robot.model,
                              kind: robot.kind,
                              type: robot.type,
                          }
                        : undefined,
                );
                setPlacement(
                    robot
                        ? {
                              departmentId: robot.departmentId,
                              lifecycle: robot.lifecycle ?? "active",
                          }
                        : undefined,
                );
                setIntegration(
                    robot
                        ? {
                              integration: robot.integration ?? "none",
                              endpoint: robot.endpoint ?? "",
                          }
                        : undefined,
                );
            }
        }, [open, robot]);

        const onCancel = (): void => onOpenChange(false);

        // A step can be jumped to directly once its section has been filled in
        // (all sections are pre-filled when editing an existing robot). Earlier
        // steps and the current one always stay navigable.
        const filledSteps = [identity != null, placement != null, integration != null];
        const nav: StepNavigation = {
            steps: STEPS.map((stepTitle) => t(stepTitle)),
            step,
            isStepEnabled: (index) => index <= step || filledSteps[index],
            onNavigate: setStep,
        };

        return (
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="bg-neutral-50 sm:max-w-3xl">
                    <DialogTitle>
                        {robot
                            ? t("page.robots.formDialog.editTitle")
                            : t("page.robots.formDialog.addTitle")}
                    </DialogTitle>
                    {step === 0 && (
                        <IdentityStep
                            nav={nav}
                            defaultValues={identity}
                            onSubmit={setIdentity}
                            onCancel={onCancel}
                        />
                    )}
                    {step === 1 && (
                        <PlacementStep
                            nav={nav}
                            defaultValues={placement}
                            onSubmit={setPlacement}
                            onCancel={onCancel}
                        />
                    )}
                    {step === 2 && (
                        <IntegrationStep
                            nav={nav}
                            defaultValues={integration}
                            onSubmit={(values) => {
                                setIntegration(values);
                                if (identity != null && placement != null) {
                                    onSave({ ...identity, ...placement, ...values });
                                    onOpenChange(false);
                                }
                            }}
                            onCancel={onCancel}
                            submitLabel={
                                robot
                                    ? t("page.robots.formDialog.saveChanges")
                                    : t("page.robots.formDialog.addRobot")
                            }
                        />
                    )}
                </DialogContent>
            </Dialog>
        );
    },
);
